<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Insurance Partner' : 'Create Insurance Partner' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-card class="form-card">
      <div class="form-card-head">Company Information</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>COMPANY NAME <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_th" placeholder="เช่น เอไอเอ ประเทศไทย"/></v-col>
          <v-col cols="12" md="6"><label>COMPANY NAME <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_en" placeholder="e.g. AIA Thailand"/></v-col>
          <v-col cols="12" md="6"><label>INSURANCE TYPE <span class="required">*</span></label><v-select dense outlined hide-details :items="insuranceTypeOptions" item-text="text" item-value="value" v-model="form.type"/></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Logo Upload</div>
      <v-card-text class="pt-4">
        <input ref="logoInput" type="file" accept="image/png,image/jpeg,image/webp" class="d-none" @change="onLogoFileChange" />
        <div class="upload-box" @click="openLogoPicker" @dragover.prevent @drop.prevent="onLogoDrop">
          <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
          Drop logo here or <b>Browse</b><br/>Upload PNG with transparent background — <b>stored on OBS</b>, Max file size: 256 KB
        </div>
        <div v-if="logoPreview" class="logo-preview mt-3">
          <img :src="logoPreview" alt="logo" />
          <button type="button" class="thumb-remove" @click="removeLogo">×</button>
        </div>
        <div v-if="uploadError" class="upload-error mt-2">{{ uploadError }}</div>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head mapping-head">
        <div>
          Branch Eligibility Mapping
          <div class="mapping-sub">Selected branches will display this partner's logo on their public hospital website.</div>
        </div>
        <span class="selected-pill">{{ selectedBranchCount }} of {{ orgOptions.length }} selected</span>
      </div>
      <v-card-text class="pt-4">
        <div class="select-all-row mb-3">
          <input id="select-all-branches" type="checkbox" :checked="selectAllBranches" @change="onSelectAllChange($event.target.checked)" />
          <label for="select-all-branches" class="branch-label mb-0">Select All Branches</label>
        </div>
        <div class="branch-grid">
          <div v-for="org in orgOptions" :key="org.value" class="branch-item">
            <input :id="`branch-${org.value}`" type="checkbox" :value="org.value" v-model="form.org_codenames" @change="syncSelectAll" />
            <label :for="`branch-${org.value}`" class="branch-label mb-0">
              <span class="code">{{ org.value }}</span>
              <span>— {{ org.text }}</span>
            </label>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save Partner</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  name_th: '',
  name_en: '',
  type: 'life',
  logo_url: '',
  org_codenames: []
})

const MAX_LOGO_SIZE_BYTES = 256 * 1024  //256 KB
const INSURANCE_UPLOAD_FOLDER = 'patient-services/insurance-partners'

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return {
      form: defaultForm(),
      logoPreview: '',
      uploadError: '',
      selectAllBranches: false,
      insuranceTypeOptions: [
        {text: 'Life Insurance', value: 'life'},
        {text: 'Health Insurance', value: 'health'},
        {text: 'Broker', value: 'broker'}
      ],
      initialLoading: false
    }
  },
  computed: {
    ...mapState('insurancePartners', ['list', 'isSaving', 'orgOptions']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    canSave() { return this.isEditMode ? hasPermision('CanEditInsurancePartner') : hasPermision('CanAddInsurancePartner') },
    selectedBranchCount() {
      return Array.isArray(this.form.org_codenames) ? this.form.org_codenames.length : 0
    },
    isFormValid() {
      return !!(this.form.name_th && this.form.name_en && this.form.type)
    }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('insurance-partners')
      await this.fetchOrgOptions()
      await this.fetch()
      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) {
          const normalizedType = target.type || target.insurance_type || 'life'
          this.form = {
            ...defaultForm(),
            ...target,
            type: normalizedType,
            org_codenames: Array.isArray(target.org_codenames) ? target.org_codenames : []
          }
          this.logoPreview = target.logo_url || ''
        }
      }
      this.syncSelectAll()
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('insurancePartners', ['setResource', 'fetch', 'save', 'fetchOrgOptions', 'upload']),
    syncSelectAll() {
      this.selectAllBranches = this.orgOptions.length > 0 && this.selectedBranchCount === this.orgOptions.length
    },
    onSelectAllChange(value) {
      this.form.org_codenames = value ? this.orgOptions.map(item => item.value) : []
    },
    openLogoPicker() {
      this.$refs.logoInput && this.$refs.logoInput.click()
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadImage(file, namePrefix) {
      const base64 = await this.fileToDataUrl(file)
      const uploadName = `${namePrefix}_${Date.now()}`
      const imageUrl = await this.upload({
        name: uploadName,
        base64,
        folder: INSURANCE_UPLOAD_FOLDER
      })
      if (!imageUrl) {
        this.uploadError = 'Upload failed. Please try again.'
      }
      return imageUrl
    },
    validateLogo(file) {
      if (!file || !file.type.startsWith('image/')) {
        this.uploadError = 'Please select an image file.'
        return false
      }
      if (file.size > MAX_LOGO_SIZE_BYTES) {
        this.uploadError = 'Max file size: 256 KB.'
        return false
      }
      return true
    },
    async onLogoFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file || !this.validateLogo(file)) return
      this.uploadError = ''
      const imageUrl = await this.uploadImage(file, 'insurance_partner_logo')
      if (!imageUrl) return
      this.logoPreview = imageUrl
      this.form.logo_url = imageUrl
      event.target.value = null
    },
    async onLogoDrop(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (!file || !this.validateLogo(file)) return
      this.uploadError = ''
      const imageUrl = await this.uploadImage(file, 'insurance_partner_logo')
      if (!imageUrl) return
      this.logoPreview = imageUrl
      this.form.logo_url = imageUrl
    },
    removeLogo() {
      this.logoPreview = ''
      this.form.logo_url = ''
    },
    onCancel() {
      this.$router.push({name: 'insurance-partner-list'})
    },
    async onSave() {
      const payload = {
        ...this.form,
        org_codenames: Array.isArray(this.form.org_codenames) ? this.form.org_codenames : []
      }
      const result = await this.$store.dispatch('insurancePartners/save', payload)
      if (!result.ok) return
      this.$router.push({name: 'insurance-partner-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.subhead { color: #6d7f9e; font-size: 14px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e5ebf6; border-radius: 12px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 18px; font-weight: 700; color: #0f1f3b; background: #fff; }
.mapping-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.mapping-sub { font-size: 12px; color: #7586a8; font-weight: 500; margin-top: 4px; }
.selected-pill { background: #eef4ff; color: #5d6f90; border-radius: 999px; padding: 8px 14px; font-size: 14px; font-weight: 700; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.upload-box { border: 1.5px dashed #d9e5f8; border-radius: 10px; color: #7a8cad; padding: 22px; text-align: center; font-size: 14px; cursor: pointer; }
.upload-icon { display: block; margin: 0 auto 8px; color: #9ab0d6; }
.logo-preview { width: 120px; height: 72px; border-radius: 8px; border: 1px solid #e5ebf6; overflow: hidden; position: relative; }
.logo-preview img { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.thumb-remove { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; border: 1px solid #f2bcbc; background: #fff; color: #c62828; line-height: 16px; font-size: 14px; cursor: pointer; }
.upload-error { color: #c62828; font-size: 12px; }
.branch-grid { border: 1px solid #edf2fb; border-radius: 10px; padding: 12px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px 20px; }
.select-all-row { display: inline-flex; align-items: center; gap: 10px; }
.branch-item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #1f3459; }
.branch-label { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-size: 14px; color: #1f3459 !important; font-weight: 400 !important; margin-bottom: 0 !important; }
.branch-item .code { color: #1e49ba; font-weight: 700; }
.required { color: #c62828; }
.lang { color: #2962ff; }
label { font-size: 12px; color: #64748b; font-weight: 600; display: block; margin-bottom: 6px; }
.actions-card { margin-bottom: 0; }
@media (max-width: 1100px) { .branch-grid { grid-template-columns: repeat(2, 1fr); } }
</style>
